import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/app/api";
import { pricingKeys } from "../keys/pricing";
import type {
  ClientGroupPriceCreateRequest,
  ClientGroupPriceForGroupCreateRequest,
  ClientGroupPriceUpdateRequest,
  ClientGroupPriceResponse,
  EffectivePartnerPricesResponse,
  PartnerPriceForPartnerCreateRequest,
  PartnerPriceForProductCreateRequest,
  PartnerPriceResponse,
  PartnerPriceUpdateRequest,
} from "@/app/api/types/pricing";

// =============================================================================
// Product Pricing Hooks
// =============================================================================

/**
 * Get all prices (group + partner) for a product (with optional user scope)
 */
export function useProductPrices(
  productId: string | null,
  enabled: boolean = true,
  userId?: string,
) {
  return useQuery({
    queryKey: userId
      ? [...pricingKeys.productPrice(productId!), "user", userId]
      : pricingKeys.productPrice(productId!),
    queryFn: () => api.pricing.getProductPrices(productId!, userId),
    enabled: !!productId && enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get effective price for a product-partner combination
 */
export function useEffectivePrice(
  productId: string | null,
  partnerId: string | null,
  targetDate?: string,
) {
  return useQuery({
    queryKey: pricingKeys.effectivePrice(productId!, partnerId!),
    queryFn: () =>
      api.pricing.getEffectivePrice(productId!, partnerId!, targetDate),
    enabled: !!productId && !!partnerId,
  });
}

/**
 * Create a client group price for a product (with optional user scope)
 */
export function useCreateGroupPrice(productId: string, userId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ClientGroupPriceCreateRequest) =>
      api.pricing.createGroupPrice(productId, data, userId),
    onSuccess: (newPrice) => {
      queryClient.invalidateQueries({
        queryKey: pricingKeys.productPrice(productId),
      });
      const key = userId
        ? pricingKeys.userGroupPricesGlobal(userId)
        : pricingKeys.companyGroupPricesGlobal();
      queryClient.setQueryData<ClientGroupPriceResponse[]>(key, (old) => {
        const arr = old || [];
        const idx = arr.findIndex((p) => p.id === newPrice.id);
        return idx >= 0
          ? arr.map((p) => (p.id === newPrice.id ? newPrice : p))
          : [...arr, newPrice];
      });
    },
  });
}

/**
 * Update a client group price
 */
export function useUpdateGroupPrice(productId: string, userId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      priceId,
      data,
    }: {
      priceId: string;
      data: ClientGroupPriceUpdateRequest;
    }) => api.pricing.updateGroupPrice(productId, priceId, data),
    onSuccess: (updatedPrice) => {
      queryClient.invalidateQueries({
        queryKey: pricingKeys.productPrice(productId),
      });
      const key = userId
        ? pricingKeys.userGroupPricesGlobal(userId)
        : pricingKeys.companyGroupPricesGlobal();
      queryClient.setQueryData<ClientGroupPriceResponse[]>(key, (old) =>
        (old || []).map((p) => (p.id === updatedPrice.id ? updatedPrice : p)),
      );
    },
  });
}

/**
 * Delete a client group price
 */
export function useDeleteGroupPrice(productId: string, userId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (priceId: string) =>
      api.pricing.deleteGroupPrice(productId, priceId),
    onSuccess: (_data, priceId) => {
      queryClient.invalidateQueries({
        queryKey: pricingKeys.productPrice(productId),
      });
      const key = userId
        ? pricingKeys.userGroupPricesGlobal(userId)
        : pricingKeys.companyGroupPricesGlobal();
      queryClient.setQueryData<ClientGroupPriceResponse[]>(key, (old) =>
        (old || []).filter((p) => p.id !== priceId),
      );
    },
  });
}

/**
 * Create a partner-specific price for a product (with optional user scope)
 */
export function useCreatePartnerPriceForProduct(
  productId: string,
  userId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PartnerPriceForProductCreateRequest) =>
      api.pricing.createPartnerPriceForProduct(productId, data, userId),
    onSuccess: (newPrice) => {
      queryClient.invalidateQueries({
        queryKey: pricingKeys.productPrice(productId),
      });
      const key = userId
        ? pricingKeys.userPartnerPricesGlobal(userId)
        : pricingKeys.companyPartnerPricesGlobal();
      queryClient.setQueryData<PartnerPriceResponse[]>(key, (old) => {
        const arr = old || [];
        const idx = arr.findIndex((p) => p.id === newPrice.id);
        return idx >= 0
          ? arr.map((p) => (p.id === newPrice.id ? newPrice : p))
          : [...arr, newPrice];
      });
    },
  });
}

/**
 * Update a partner-specific price (from product context)
 */
export function useUpdatePartnerPriceForProduct(
  productId: string,
  userId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      priceId,
      data,
    }: {
      priceId: string;
      data: PartnerPriceUpdateRequest;
    }) => api.pricing.updatePartnerPriceForProduct(productId, priceId, data),
    onSuccess: (updatedPrice) => {
      queryClient.invalidateQueries({
        queryKey: pricingKeys.productPrice(productId),
      });
      const key = userId
        ? pricingKeys.userPartnerPricesGlobal(userId)
        : pricingKeys.companyPartnerPricesGlobal();
      queryClient.setQueryData<PartnerPriceResponse[]>(key, (old) =>
        (old || []).map((p) => (p.id === updatedPrice.id ? updatedPrice : p)),
      );
    },
  });
}

/**
 * Delete a partner-specific price (from product context)
 */
export function useDeletePartnerPriceForProduct(
  productId: string,
  userId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (priceId: string) =>
      api.pricing.deletePartnerPriceForProduct(productId, priceId),
    onSuccess: (_data, priceId) => {
      queryClient.invalidateQueries({
        queryKey: pricingKeys.productPrice(productId),
      });
      const key = userId
        ? pricingKeys.userPartnerPricesGlobal(userId)
        : pricingKeys.companyPartnerPricesGlobal();
      queryClient.setQueryData<PartnerPriceResponse[]>(key, (old) =>
        (old || []).filter((p) => p.id !== priceId),
      );
    },
  });
}

// =============================================================================
// Partner Pricing Hooks
// =============================================================================

/**
 * Get all product prices for a partner (with optional user scope)
 */
export function usePartnerPrices(partnerId: string | null, userId?: string) {
  return useQuery({
    queryKey: userId
      ? [...pricingKeys.partnerPrice(partnerId!), "user", userId]
      : pricingKeys.partnerPrice(partnerId!),
    queryFn: () => api.pricing.getPartnerPrices(partnerId!, userId),
    enabled: !!partnerId,
  });
}

/**
 * Create a product price for a partner (with optional user scope)
 */
export function useCreatePartnerProductPrice(
  partnerId: string,
  userId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PartnerPriceForPartnerCreateRequest) =>
      userId
        ? api.pricing.createPartnerProductPriceScoped(partnerId, data, userId)
        : api.pricing.createPartnerProductPrice(partnerId, data),
    onSuccess: (newPrice) => {
      queryClient.invalidateQueries({
        queryKey: pricingKeys.partnerPrice(partnerId),
      });
      queryClient.invalidateQueries({
        queryKey: pricingKeys.effectivePartnerPrices(partnerId),
      });
      const key = userId
        ? pricingKeys.userPartnerPricesGlobal(userId)
        : pricingKeys.companyPartnerPricesGlobal();
      queryClient.setQueryData<PartnerPriceResponse[]>(key, (old) => {
        const arr = old || [];
        const idx = arr.findIndex((p) => p.id === newPrice.id);
        return idx >= 0
          ? arr.map((p) => (p.id === newPrice.id ? newPrice : p))
          : [...arr, newPrice];
      });
    },
  });
}

/**
 * Update a product price for a partner (with optional user scope for cache invalidation)
 */
export function useUpdatePartnerProductPrice(
  partnerId: string,
  userId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      priceId,
      data,
    }: {
      priceId: string;
      data: PartnerPriceUpdateRequest;
    }) => api.pricing.updatePartnerProductPrice(partnerId, priceId, data),
    onMutate: async ({ priceId, data }) => {
      // Build exact cache key matching what useEffectivePartnerPrices uses
      const effectiveKey = userId
        ? [...pricingKeys.effectivePartnerPrices(partnerId), "user", userId]
        : pricingKeys.effectivePartnerPrices(partnerId);

      // Cancel outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: pricingKeys.effectivePartnerPrices(partnerId),
      });
      await queryClient.cancelQueries({
        queryKey: pricingKeys.partnerPrice(partnerId),
      });

      // Snapshot previous values
      const previousEffective =
        queryClient.getQueryData<EffectivePartnerPricesResponse>(effectiveKey);

      // Optimistically update effectivePartnerPrices
      if (
        previousEffective &&
        data.price !== undefined &&
        data.price !== null
      ) {
        queryClient.setQueryData<EffectivePartnerPricesResponse>(effectiveKey, {
          ...previousEffective,
          prices: previousEffective.prices.map((item) =>
            item.source_id === priceId ? { ...item, price: data.price! } : item,
          ),
        });
      }

      return { previousEffective, effectiveKey };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousEffective) {
        queryClient.setQueryData(
          context.effectiveKey,
          context.previousEffective,
        );
      }
    },
    onSettled: () => {
      // Refetch to ensure server state
      queryClient.invalidateQueries({
        queryKey: pricingKeys.partnerPrice(partnerId),
      });
      queryClient.invalidateQueries({
        queryKey: pricingKeys.effectivePartnerPrices(partnerId),
      });
      // Invalidate the correct scope's global cache
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: pricingKeys.userPartnerPricesGlobal(userId),
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: pricingKeys.companyPartnerPricesGlobal(),
          exact: true,
        });
      }
    },
  });
}

/**
 * Delete a product price for a partner (with optional user scope for cache invalidation)
 */
export function useDeletePartnerProductPrice(
  partnerId: string,
  userId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (priceId: string) =>
      api.pricing.deletePartnerProductPrice(partnerId, priceId),
    onSuccess: (_data, priceId) => {
      queryClient.invalidateQueries({
        queryKey: pricingKeys.partnerPrice(partnerId),
      });
      queryClient.invalidateQueries({
        queryKey: pricingKeys.effectivePartnerPrices(partnerId),
      });
      const key = userId
        ? pricingKeys.userPartnerPricesGlobal(userId)
        : pricingKeys.companyPartnerPricesGlobal();
      queryClient.setQueryData<PartnerPriceResponse[]>(key, (old) =>
        (old || []).filter((p) => p.id !== priceId),
      );
    },
  });
}

/**
 * Get all effective prices for a partner (GROUP and PARTNER prices only, with optional user scope)
 */
export function useEffectivePartnerPrices(
  partnerId: string | null,
  userId?: string,
) {
  return useQuery({
    queryKey: userId
      ? [...pricingKeys.effectivePartnerPrices(partnerId!), "user", userId]
      : pricingKeys.effectivePartnerPrices(partnerId!),
    queryFn: () => api.pricing.getEffectivePartnerPrices(partnerId!, userId),
    enabled: !!partnerId,
  });
}

// =============================================================================
// Client Group Pricing Hooks
// =============================================================================

/**
 * Get all product prices for a client group (with optional user scope)
 */
export function useClientGroupPrices(groupId: string | null, userId?: string) {
  return useQuery({
    queryKey: userId
      ? [...pricingKeys.clientGroupPrices(groupId!), "user", userId]
      : pricingKeys.clientGroupPrices(groupId!),
    queryFn: () =>
      userId
        ? api.pricing.getClientGroupPricesScoped(groupId!, userId)
        : api.pricing.getClientGroupPrices(groupId!),
    enabled: !!groupId,
  });
}

/**
 * Create a product price for a client group (with optional user scope)
 */
export function useCreateClientGroupProductPrice(
  groupId: string,
  userId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ClientGroupPriceForGroupCreateRequest) =>
      userId
        ? api.pricing.createClientGroupProductPriceScoped(groupId, data, userId)
        : api.pricing.createClientGroupProductPrice(groupId, data),
    onSuccess: (newPrice) => {
      queryClient.invalidateQueries({
        queryKey: pricingKeys.clientGroupPrices(groupId),
      });
      const key = userId
        ? pricingKeys.userGroupPricesGlobal(userId)
        : pricingKeys.companyGroupPricesGlobal();
      queryClient.setQueryData<ClientGroupPriceResponse[]>(key, (old) => {
        const arr = old || [];
        const idx = arr.findIndex((p) => p.id === newPrice.id);
        return idx >= 0
          ? arr.map((p) => (p.id === newPrice.id ? newPrice : p))
          : [...arr, newPrice];
      });
    },
  });
}

/**
 * Update a product price for a client group
 */
export function useUpdateClientGroupProductPrice(
  groupId: string,
  userId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      priceId,
      data,
    }: {
      priceId: string;
      data: ClientGroupPriceUpdateRequest;
    }) => api.pricing.updateClientGroupProductPrice(groupId, priceId, data),
    onSuccess: (updatedPrice) => {
      queryClient.invalidateQueries({
        queryKey: pricingKeys.clientGroupPrices(groupId),
      });
      const key = userId
        ? pricingKeys.userGroupPricesGlobal(userId)
        : pricingKeys.companyGroupPricesGlobal();
      queryClient.setQueryData<ClientGroupPriceResponse[]>(key, (old) =>
        (old || []).map((p) => (p.id === updatedPrice.id ? updatedPrice : p)),
      );
    },
  });
}

/**
 * Delete a product price for a client group
 */
export function useDeleteClientGroupProductPrice(
  groupId: string,
  userId?: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (priceId: string) =>
      api.pricing.deleteClientGroupProductPrice(groupId, priceId),
    onSuccess: (_data, priceId) => {
      queryClient.invalidateQueries({
        queryKey: pricingKeys.clientGroupPrices(groupId),
      });
      const key = userId
        ? pricingKeys.userGroupPricesGlobal(userId)
        : pricingKeys.companyGroupPricesGlobal();
      queryClient.setQueryData<ClientGroupPriceResponse[]>(key, (old) =>
        (old || []).filter((p) => p.id !== priceId),
      );
    },
  });
}
